import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSetRecoilState } from 'recoil';
import { DialogMessageState } from '../../recoil/states';
import { asyncReqCommentReg } from '../../reducers/CommentRegReducer';
import { AppDispatch, RootState } from '../../Store';
import CommonUtil from '../../utils/CommonUtil';
import Emoji from '../../components/ui/Emoji';
import { useKeyboardAwareFocus } from '../../hooks/useKeyboardAwareFocus';

type Props = {
    comment_seq?: string;
}

const CommentReg = ({ comment_seq = "" }: Props) => {

    const { MEM_UDID, NICKNAME } = useSelector((state: RootState) => state.LoginBtnsReducer.asyncIsLogin); // '로그인 / 로그아웃' 버튼 표시 처리를 위한 state
    const setDialogMessageState = useSetRecoilState(DialogMessageState); // 다일얼로그 메세지 요청 정보를 갖는 state
    const dispatch = useDispatch<AppDispatch>();

    const refBtnReg = useRef<HTMLAnchorElement>(null); // 댓글등록 링크 버튼 참조 객체

    // 입력박스에 포커스가 들어가면 소프트키패드 상단으로 이동 / 해당 입력박스가 가시영역에서 사라지면 소프트키패드 숨김 처리해주는 hook
    useKeyboardAwareFocus({ minVisiblePx: 24, autoBlurIfOutOfView: true });

    /**
    * TextBox 폼 체크
    * @param {HTMLInputElement | null} refObj ref 객체
    * @param {string} name ref 객체명
    * @returns {boolean}
    */
    const TextBoxConfirm = (refObj: HTMLInputElement | null, name: string): boolean => {

        if (refObj?.value.trim() === "") {

            const message = name + " 입력하세요.";
            const callbackFuncOk = () => {
                refObj.value = "";
                refObj.focus();
            };
            setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message, callbackFuncOk }));

            return false;
        }
        return true;
    }

    /**
    * 댓글등록 입력박스 클릭 이벤트 
    * 
    * @param {React.MouseEvent<HTMLInputElement, MouseEvent>} e 이벤트 객체
    * @returns {void}
    */
    const handlerCommentInptClick = (e: React.MouseEvent<HTMLInputElement, MouseEvent>): void => {

        if (MEM_UDID === "") {
            const message = "로그인 후 이용 가능합니다.";
            setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
            $(e.target).trigger("blur");
            return;
        }
    }

    /**
    * 댓글등록 버튼 클릭 이벤트 
    * 
    * @param {React.MouseEvent<HTMLAnchorElement, MouseEvent>} e 이벤트 객체
    * @returns {void}
    */
    const handlerCommentReg = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
        e.preventDefault();

        if (MEM_UDID === "") {
            const message = "로그인 후 이용 가능합니다.";
            setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
            return;
        }

        const txt_Reg_Nickname = $("#txt_Reg_Nickname_" + comment_seq);
        const txt_Reg_Comment = $("#txt_Reg_Comment_" + comment_seq);

        const board_seq = CommonUtil.getQuerystring("seq", "");
        const ref_comment_seq = comment_seq;
        const nickname = ((txt_Reg_Nickname.val() as string) + "").trim();
        const comment = ((txt_Reg_Comment.val() as string) + "").trim();
        const reg_udid = MEM_UDID;

        if (txt_Reg_Nickname.is(":visible") && nickname === "") {

            TextBoxConfirm(txt_Reg_Nickname.get(0) as HTMLInputElement, "'별명'을");
            return;
        }
        if (comment === "") {

            TextBoxConfirm(txt_Reg_Comment.get(0) as HTMLInputElement, "'댓글 내용'을");
            return;
        }

        dispatch(asyncReqCommentReg({ board_seq, ref_comment_seq, nickname, comment, reg_udid }));
    }

    /**
    * 별명변경 버튼 클릭 이벤트 
    * 
    * @param {React.MouseEvent<HTMLAnchorElement, MouseEvent>} e 이벤트 객체
    * @returns {void}
    */
    const handlerNicknameMod = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
        e.preventDefault();

        const AppWebView = (window as any).AppWebView;
        if (typeof (AppWebView) !== "undefined") {
            if (typeof (AppWebView.show_Nickname_Mod) === "function") {
                AppWebView.show_Nickname_Mod();
            }
        }
    }

    /**
    * 이모지 표시 버튼 클릭 이벤트
    * 
    * @param {React.MouseEvent<HTMLAnchorElement, MouseEvent>} e 이벤트 객체
    * @returns {void}
    */
    const handlerShowEmoji = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {

        if (MEM_UDID === "") {
            const message = "로그인 후 이용 가능합니다.";
            setDialogMessageState(rawState => ({ ...rawState, isShow: true, isConfirm: false, message }));
            return;
        }

        // 이모지 영역 표시 or 숨김
        const root_emoji = $("#root_emoji_" + comment_seq);
        if (root_emoji.is(":visible")) root_emoji.hide();
        else root_emoji.show();

        root_emoji.find(".content_emoji").animate({ scrollTop: 0 }, 0); // 이모지 최상단으로 스크롤바 이동
    }

    /**
    * 입력창 엔터 키 체크 이벤트
    * 
    * @param e 이벤트 객체
    * @returns 
    */
    const handlerInputEnterProc = (e: React.KeyboardEvent<HTMLInputElement>, tagetId: string) => {

        const btnEl: HTMLAnchorElement | null = refBtnReg.current;
        if (e.key === "Enter") {
            $(e.target).trigger("blur");
            btnEl?.click();
        }
    }

    return (
        <aside className="bo_vc_w">
            <h2>댓글쓰기</h2>
            <div className="bo_vc_w_info" style={navigator.userAgent.indexOf(";UGGIYO_APP;") > -1 && NICKNAME === "" ? {} : { display: "none" }}>
                <input type="text" className="wr_name frm_input required" maxLength={15} placeholder="별명" id={"txt_Reg_Nickname_" + comment_seq} />
            </div>
            <input type="text" id={"txt_Reg_Comment_" + comment_seq} onClick={handlerCommentInptClick} onKeyDown={e => handlerInputEnterProc(e, "txt_Reg_Comment_" + comment_seq)} className="wr_name frm_input required" maxLength={100} placeholder="댓글내용을 입력해주세요." style={{ width: "100%" }} enterKeyHint="done" />
            <div className="bo_vc_w_wr">
                <a href="#none" className="btn_submit" ref={refBtnReg} onClick={handlerCommentReg}>댓글등록</a>
                {
                    navigator.userAgent.indexOf(";UGGIYO_APP;") > -1 && NICKNAME !== "" ?
                        <a href="#none" className="btn_submit" onClick={handlerNicknameMod}>별명변경</a>
                        : <></>
                }
                <button type="button" className="btn_emoji" onClick={handlerShowEmoji}>
                    <div>
                        <svg aria-label="이모티콘" fill="#262626" height="24" role="img" viewBox="0 0 48 48" width="24">
                            <path d="M24 48C10.8 48 0 37.2 0 24S10.8 0 24 0s24 10.8 24 24-10.8 24-24 24zm0-45C12.4 3 3 12.4 3 24s9.4 21 21 21 21-9.4 21-21S35.6 3 24 3z"></path>
                            <path d="M34.9 24c0-1.4-1.1-2.5-2.5-2.5s-2.5 1.1-2.5 2.5 1.1 2.5 2.5 2.5 2.5-1.1 2.5-2.5zm-21.8 0c0-1.4 1.1-2.5 2.5-2.5s2.5 1.1 2.5 2.5-1.1 2.5-2.5 2.5-2.5-1.1-2.5-2.5zM24 37.3c-5.2 0-8-3.5-8.2-3.7-.5-.6-.4-1.6.2-2.1.6-.5 1.6-.4 2.1.2.1.1 2.1 2.5 5.8 2.5 3.7 0 5.8-2.5 5.8-2.5.5-.6 1.5-.7 2.1-.2.6.5.7 1.5.2 2.1 0 .2-2.8 3.7-8 3.7z"></path>
                        </svg>
                    </div>
                </button>
                <Emoji comment_seq={comment_seq} />
            </div>
        </aside>
    )
}

export default CommentReg;
